
import { KeenIcon, DefaultTooltip, LazyFormikSelect, DatePicker, CustomOption, CustomSingleValue } from '@/components';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import * as Yup from "yup";
import { Formik, Form, Field, ErrorMessage, getIn } from "formik";
import { Link, useNavigate, useParams } from 'react-router-dom';
import CreatableSelect from 'react-select/creatable';
import { toast } from 'sonner';
import { useContactOptions, useLazyOptions } from '@/hooks';
import { useAbility } from '@/providers/';
import { Can } from '@casl/react';
import expenseService from '@/services/billing/expenses/expenseService';
import matterService from '@/services/matter/matterService';
import categoryService from '@/services/billing/expenses/categoryService';
import contactTypeService from '@/services/contact/contactTypeService';
import userService from '@/services/setting/userService';
import { CategoryModal } from '@/pages/billing';
import invoiceService from '@/services/billing/invoice/invoiceService';
import { formatDateToYMD, calculateSubtotal } from '@/utils';
import { useUserOptions } from '@/hooks'

const validationSchema = Yup.object().shape({
    contact_id: Yup.string()
        .required('Contact is required'),
    matter_id: Yup.string()
        .required('Matter is required'),
    category_id: Yup.string()
        .required('Category is required'),
    date: Yup.date()
        .required('Date is required')
        .typeError('Invalid date format'),
    quantity: Yup.number()
        .required('Quantity is required')
        .min(1, 'Quantity must be at least 1')
        .typeError('Quantity must be a number'),
    price: Yup.number()
        .required('Price is required')
        .min(0, 'Price cannot be negative')
        .typeError('Price must be a number'),
});

const EditExpense = () => {
    const [customFields, setCustomFields] = useState([]);
    const [dataLoading, setDataLoading] = useState(false)
    // const [contactData, setContactData] = useState([]);
    const [expenseData, setExpenseData] = useState([]);
    const [userData, setuserData] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false);
    const [createContactLoading, setCreateContactLoading] = useState(false)
    const [updateFieldloading, setUpdateFieldloading] = useState(false)
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('create');
    const [selectedData, setSelectedData] = useState(null);
    const [formValues, setFormValues] = useState({});
    const [loading, setLoading] = useState(false)
    const [matterData, setMatterData] = useState([]);
    const [itemData, setItemData] = useState([]);
    const { id } = useParams();
    const expenseId = id;
    const Navigate = useNavigate()
    const { ability } = useAbility();
    const formikRef = useRef();
    const loadUserOptions = useUserOptions({ defaultPageSize: 20 });


    const handleSubmit = async (formData, resetForm, setErrors) => {
        if (expenseId) {
            setCreateContactLoading(true)
            try {
                const { data, status } = await expenseService.updateExpense(formData, expenseId);
                if (status) {
                    // resetForm()
                    toast.success('Expense updated.', {
                        description: `Expense Updated Successfully.`,
                        action: {
                            label: 'Ok',
                        },
                    });
                    Navigate('/billing/expense')
                }
            } catch (error) {
                console.error('Error', error)
                if (error.message == '422 Unprocessable Entity. Validation failed.') {
                    toast.warning('Validation Error.', {
                        description: 'Please check your input. Some fields may be invalid.',
                        action: {
                            label: 'Ok',
                        },
                    });
                    return
                }
                toast.error('Oops! Something went wrong.', {
                    description: 'An error occurred while updating expense. Please try again in a moment.',
                    action: {
                        label: 'Ok',
                    },
                });
            } finally {
                setCreateContactLoading(false);
            }
        }
    };

    const getSingleExpense = async (expenseId) => {
        setDataLoading(true)
        try {
            const { data, status } = await expenseService.getSingleExpense(expenseId);
            if (status) {
                setExpenseData(data)
            }
        } catch (error) {
            console.error('Error', error)
            toast.error('Failed to fetch Contact.', {
                description: 'An error occurred while fetching contact. Please try again later.',
            });
        } finally {
            setDataLoading(false)
        }
    }

    const loadContactOptions = useContactOptions({ defaultPageSize: 20 });

    const getAllMatters = async () => {
        setDataLoading(true)
        try {
            const { data, status } = await matterService.getAllMatters();
            if (status) {
                const Data = data.map(matter => ({
                    label: matter.name,
                    value: matter.id.toString(),
                    id: matter.id.toString(),
                }));
                setMatterData(Data)
                return Data
            }
        } catch (error) {
            console.error('Error', error)
            toast.error('Failed to fetch Matters.', {
                description: 'An error occurred while fetching matters. Please try again later.',
            });
        } finally {
            setDataLoading(false)
        }
    }
    const getAllMattersRelatedToContact = async (contactId, index) => {
        console.log('id', contactId)
        setDataLoading(true)
        try {
            const { data, status } = await invoiceService.getContactMatterRelatedData('contact', contactId);
            if (status) {
                return data.matters.map(m => ({
                    label: m.name,
                    value: m.id.toString(),
                    id: m.id.toString(),
                }));
            }
            return [];
        } catch (error) {
            console.error('Error', error)
            toast.error('Failed to fetch Matters.', {
                description: 'An error occurred while fetching matters. Please try again later.',
            });
        } finally {
            setDataLoading(false)
        }
    }

    const {
        data: matterOptions,
        loading: matterLoading,
        load: loadMatters,
        reset: resetMatters,
    } = useLazyOptions(async (contactId) => {
        if (contactId) {
            return await getAllMattersRelatedToContact(contactId);
        } else {
            return await getAllMatters();
        }
    });

    const getAllCategories = async () => {
        setDataLoading(true)
        try {
            const { data, status } = await categoryService.getAllCategory();
            if (status) {
                const Data = data.map(item => ({
                    label: item.category_name,
                    value: item.id.toString(),
                    id: item.id.toString(),
                }));
                setItemData(Data)
                return Data
            }
        } catch (error) {
            console.log(error)
            // if (error.status && Array.isArray(error.data)) {
            //     const fallbackData = error.data.map(item => ({
            //         label: item.category_name,
            //         value: item.id.toString(),
            //         id: item.id.toString(),
            //     }));
            //     setItemData(fallbackData);
            // } else {
            toast.error('Failed to fetch Expense Categories.', {
                description: 'An error occurred while fetching expense categories. Please try again later.',

            });
            // }
        } finally {
            setDataLoading(false)
        }
    }
    const {
        data: categoryOptions,
        loading: categoryLoading,
        load: loadCategories
    } = useLazyOptions(getAllCategories);

    const handleModalClose = () => {
        setItemId(null)
        setSelectedData(null)
        setMode('create')
        setOpen(false)
    }

    const handleSubmitCategory = async (formData) => {
        setIsLoading(true)
        try {
            const { data, status } = await categoryService.createCategory(formData);
            if (status) {
                setOpen(false)
                // getAllContactType()
                toast.success('Category Created Successfully.', {
                    description: `${data.category_name} Category Created Successfully.`,
                    action: {
                        label: 'Ok',
                    },
                });
            }
        } catch (error) {
            console.error('Error', error)
            if (error.message == '422 Unprocessable Entity. Validation failed.') {
                toast.warning('Validation Error.', {
                    description: 'Please check your input. Some fields may be invalid.',
                });
            }
            toast.error('Failed to Create Category.', {
                description: 'An error occurred while creating category. Please try again later.',
            });
        } finally {
            setIsLoading(false)
        }
    };

    useEffect(() => {
        if (expenseId) { getSingleExpense(expenseId); }
    }, [])

    const initialValues = {
        contact_id: expenseData?.contact_id || '',
        contact: expenseData?.contact
            ? {
                label: `${expenseData.contact.first_name} ${expenseData.contact.last_name}`,
                value: expenseData.contact.id,
                id: expenseData.contact.id
            }
            : null,
        matter_id: expenseData?.matter_id || '',
        matter: expenseData?.matter
            ? {
                label: expenseData.matter.name,
                value: expenseData.matter.id,
                id: expenseData.matter.id
            }
            : null,
        category_id: expenseData?.category_id || '',
        category: expenseData?.category
            ? {
                label: expenseData.category.category_name,
                value: expenseData.category.id,
                id: expenseData.category.id
            }
            : null,
        cost_type: expenseData?.cost_type || 'soft cost',
        date: formatDateToYMD(expenseData?.date || ''),
        quantity: expenseData?.quantity || 1,
        rate: expenseData?.rate || '',
        billed_by: expenseData?.billed_by?.id || '',
        billed_by_user: expenseData?.billed_by
            ? {
                label: `${expenseData.billed_by.first_name} ${expenseData.billed_by.last_name}`,
                value: expenseData.billed_by.id,
                id: expenseData.billed_by.id,
                email: expenseData.billed_by.email
            } : null,
        status: expenseData?.status || 'billable',
        description: expenseData?.description || '',
        note: expenseData?.note || '',
    };

    return (
        <>
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values, { resetForm, setErrors }) => {
                    // return console.log("Form Submitted:", payload); 
                    handleSubmit(values, resetForm, setErrors);
                }}
            >
                {({ formik, errors, values, handleChange, handleSubmit, setFieldValue, setValues, setTouched, setErrors }) => {
                    useEffect(() => {
                        setValues((prev) => ({
                            ...initialValues,
                            ...formValues,
                        }));
                    }, [selectedData]); // Only runs when fields change

                    useEffect(() => {
                        // Sync latest values into external state
                        setFormValues(values);
                    }, [values]);
                    return (
                        <Form onSubmit={handleSubmit} className='px-2.5'>
                            <div className="card pb-2.5">
                                <div className="card-header">
                                    <h3 className="card-title">Edit Expense</h3>
                                    <div>
                                        <Link to='/billing/expense/category' className='text-xs btn btn-sm btn-light'>
                                            <KeenIcon icon="plus" className="" />
                                            Add Expense Category
                                        </Link>
                                    </div>
                                </div>
                                <div className="grid gap-5 card-body">
                                    <div className='flex items-center'>
                                        <span className='w-12 h-0.5 bg-border inline-block mr-2'></span>
                                        <p className='text-sm'>Personal Information</p>
                                    </div>

                                    <div className="grid gap-5 xs:grid-cols-3">
                                        <div>
                                            <label className="mb-1 form-label">Contact <span className='pl-px text-red-600'>*</span></label>
                                            <LazyFormikSelect
                                                name={`contact_id`}
                                                placeholder="Select Contact"
                                                loadOptions={loadContactOptions}
                                                pageSize={20}
                                                preloadedOption={initialValues.contact}
                                            />
                                            <ErrorMessage
                                                name={`contact_id`}
                                                component="div"
                                                className="mt-1 text-xs text-red-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-1 form-label">Matter <span className='pl-px text-red-600'>*</span></label>
                                            {/* <FormikSelect
                        name={`expenses[${index}].matter_id`}
                        options={matterData}
                        placeholder="Select Matter"
                    /> */}
                                            <LazyFormikSelect
                                                name={`matter_id`}
                                                options={matterOptions}
                                                placeholder="Select Matter"
                                                onFocus={() => {
                                                    const contactId = getIn(values, `contact_id`);
                                                    loadMatters(contactId || null);
                                                }}
                                                isLoading={matterLoading}
                                                preloadedOption={initialValues.matter}
                                            />
                                            <ErrorMessage name={`matter_id`} component="div" className="mt-1 text-xs text-red-500" />
                                        </div>
                                        <div>
                                            <label className="mb-1 form-label">Category <span className='pl-px text-red-600'>*</span></label>
                                            {/* <FormikSelect
                        name={`expenses[${index}].category_id`}
                        options={itemData}
                        placeholder="Select expense category"
                    /> */}
                                            <LazyFormikSelect
                                                name={`category_id`}
                                                options={categoryOptions}
                                                placeholder="Select Category"
                                                onFocus={() => loadCategories(null)}
                                                isLoading={categoryLoading}
                                                noOptionsMessage={() => (
                                                    isLoading ? (
                                                        <span className="text-sm">Loading...</span>
                                                    ) : (
                                                        <div>
                                                            <span className="mr-1 text-sm">No options available</span>
                                                            <Can I='handle' a='expense' ability={ability}>
                                                                <button type="button"
                                                                    onClick={() => setOpen(true)}
                                                                    className="text-sm text-blue-500 underline"
                                                                >
                                                                    Add new
                                                                </button>
                                                            </Can>
                                                        </div>
                                                    )
                                                )}
                                                preloadedOption={initialValues.category}
                                            />
                                            <ErrorMessage name={`category_id`} component="div" className="mt-1 text-xs text-red-500" />
                                        </div>
                                        <div className='w-full'>
                                            <label className="mb-1 form-label">Billed By</label>
                                            {/* <FormikSelect
                        name={`expenses[${index}].billed_by`}
                        options={userData}
                        placeholder="Select Assignee"
                    /> */}
                                            <LazyFormikSelect
                                                name={`billed_by`}
                                                placeholder="Select Assignee"
                                                loadOptions={loadUserOptions}
                                                pageSize={20}
                                                preloadedOption={initialValues.billed_by_user}
                                                components={{
                                                    Option: CustomOption,
                                                    SingleValue: CustomSingleValue,
                                                }}
                                            />
                                            <ErrorMessage name={`billed_by`} component="div" className="mt-1 text-xs text-red-500" />
                                        </div>
                                        <div>
                                            <label className="mb-1 form-label">Quantity</label>
                                            <Field className="input" type="text" name={`quantity`} />
                                            <ErrorMessage name={`quantity`} component="div" className="mt-1 text-xs text-red-500" />
                                        </div>
                                        <div>
                                            <label className="mb-1 form-label">Price <span className='pl-px text-red-600'>*</span></label>
                                            <Field className="input" type="text" name={`price`} />
                                            <ErrorMessage name={`price`} component="div" className="mt-1 text-xs text-red-500" />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-3 gap-5">
                                        <div className=''>
                                            <label className="mb-1 form-label">Total</label>
                                            <div className='input'>
                                                {calculateSubtotal(values.rate, values.quantity)}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="mb-1 form-label">Date</label>
                                            <Field name={`date`}>
                                                {({ field, form, meta }) => (
                                                    <div>
                                                        <DatePicker field={field} form={form} isRange={false} />
                                                        {meta.touched && meta.error && (
                                                            <div className="mt-1 text-xs text-red-500">{meta.error}</div>
                                                        )}
                                                    </div>
                                                )}
                                            </Field>
                                        </div>
                                        <div>
                                            <label className="mb-1 form-label">Status</label>
                                            <Field as="select" className="input" name={`status`}>
                                                <option value="billable">Billable</option>
                                                <option value="billed">Billed</option>
                                                <option value="paid">Paid</option>
                                                <option value="not billable">Not Billable</option>

                                            </Field>
                                        </div>
                                        <div>
                                            <DefaultTooltip title={
                                                <>
                                                    <div>
                                                        <span>Soft Cost:</span> <br />
                                                        <span className='font-thin'>
                                                            Intangible expenses for your clients (travel costs, envelopes, faxes, etc…)
                                                        </span>
                                                    </div>
                                                    <br />
                                                    <div>
                                                        <span>Hard Cost:</span> <br />
                                                        <span className='font-thin'>
                                                            Expenses you need to pay a third-party vendor for (court filing fees, private investigators, mediators, etc...)
                                                        </span>
                                                    </div>
                                                </>
                                            }>
                                                <label className="items-center gap-1 mb-1 form-label">
                                                    <KeenIcon icon="information" />
                                                    Cost Type
                                                </label>
                                            </DefaultTooltip>
                                            <Field as="select" className="input" name={`cost_type`}>
                                                <option value="soft cost">Soft Cost</option>
                                                <option value="hard cost">Hard Cost</option>

                                            </Field>
                                        </div>
                                    </div>

                                    <div className='grid grid-cols-2 gap-5 mt-5'>
                                        <div>
                                            <label className="mb-1 form-label">Description</label>
                                            <Field as="textarea" className="input pt-2 h-[120px]" name="description" />
                                        </div>
                                        <div>
                                            <label className="mb-1 form-label">Note</label>
                                            <Field as="textarea" className="input pt-2 h-[120px]" name="note" />
                                        </div>
                                    </div>

                                    {/* Submit Button */}
                                    <div className="inline-flex items-center justify-end gap-2">
                                        <button type="button" disabled={isLoading || createContactLoading} onClick={() => setIsModalOpen(true)} className="btn btn-link btn-sm h-fit">
                                            Create Custom Field
                                        </button>
                                        <button type="submit" disabled={createContactLoading} className="btn btn-primary">
                                            {createContactLoading ? 'Please Wait...' : expenseId ? 'Update Expense' : 'Create Expense'}
                                        </button>
                                    </div>

                                </div>
                            </div>
                        </Form>
                    )
                }}
            </Formik>
            <CategoryModal
                open={open}
                onCloseModal={handleModalClose}
                handleMode={mode}
                data={selectedData}
                onSubmit={handleSubmitCategory}
                createFieldLoading={isLoading}
                updateFieldLoading={loading}
                ref={formikRef}
            />
        </>
    );
};
export { EditExpense };
